import { useCallback, useEffect, useMemo, useState } from "react";
import { useGetService } from "./useGetService";
import {
  API,
  DashboardTabs,
  ETransportationServiceRequestTaskType,
} from "../constants";

// Third party
// import { v4 as uuidv4 } from "uuid";
import _ from "lodash";
import { getUserData } from "../shared/utils";
import {
  EServiceRequestCategory,
  IServiceRequest,
  ITmResponse,
} from "../types/global.types";
import { useArrivalDepartureData } from "./useArrivalDepartureData";
import moment from "moment";

// type HotelDataMap = Record<string | number, boolean>;

export const useTransportDashboard = (
  startDate: Date,
  endDate: Date,
  selectedTab: string,
  subSelectedTab: string,
  appliedStartDate: Date,
  appliedEndDate: Date
) => {
  const [rows, setRows] = useState([]);
  const [isRowsLoading, setRowsLoading] = useState(false);
  const vendorId = getUserData()["cognito:username"];

  // FETCH SHIPBOARD HR REQUEST DETAILS
  const { response: shipboardAssigned, isLoading: isShipboardAssignedLoading } =
    useGetService(`${API.VENDOR_SERVICE_REQUEST.SHIPBOARD_HR_ARRIVED}s`);

  const {
    response: shuttleServiceRecords,
    isLoading: iSshuttleServiceRecordsLoading,
    error: shuttleServiceError,
  } = useGetService(`${API.HOTEL.GET_SHUTTLE_SERVICE}`);

  const {
    response,
    isLoading,
    handleSearchApply,
    refreshingText,
    error: arrivalDepartureError,
    detailedError: arrivalDepartureDetailedError,
  } = useArrivalDepartureData({
    startDate,
    endDate,
    selectedTab,
  });

  // FETCH CREATED MANUAL REQUEST DETAILS
  const {
    response: manualRequestDetails,
    isLoading: ismanualRequestDetailsLoading,
    refetch: refetchManualRequest,
  } = useGetService(
    `${API.VENDOR_SERVICE_REQUEST.CREATE_MANUAL_REQUEST}s?vendorId=${vendorId}`
  );

  const assignmentStartDate = moment(appliedStartDate); //.subtract(7, "day");
  const assignmentEndDate = moment(appliedEndDate);
  // FETCH TRANSPORT ASSIGNMENT
  const {
    response: serviceRequests,
    isLoading: isAssignedDataLoading,
    refetch: refetchAssinged,
    error,
    detailedError,
  } = useGetService(
    `${API.CARNIVAL_ADMIN.GET_SERVICE_REQUEST_BY_VENDOR_ID
    }?vendorId=${vendorId}&startDate=${assignmentStartDate.format(
      "DD/MM/YYYY"
    )}&endDate=${assignmentEndDate.format("DD/MM/YYYY")}`
  );

  const isDeparture = selectedTab === DashboardTabs.DEPARTURE;
  const assignedData = useMemo(() => {
    if (isAssignedDataLoading || !Array.isArray(serviceRequests?.data)) {
      return { data: [] };
    }

    const filteredData = serviceRequests.data.filter((x: IServiceRequest) =>
      isDeparture
        ? x.requestCategory === EServiceRequestCategory.DEPARTURE
        : x.requestCategory === EServiceRequestCategory.ARRIVAL
    );

    return { data: filteredData };
  }, [serviceRequests, isAssignedDataLoading, isDeparture]);

  const getUrgentTransportationData = useCallback(() => {
    if (ismanualRequestDetailsLoading) return [];

    const urgentRequests = manualRequestDetails.data?.filter(
      (x: any) => x.isUrgentTransportRequest
    );

    const urgentRequestList = urgentRequests.map((manualRequest: any) => {
      let serviceRequestAssigned = null;

      if (manualRequest.serviceRequestId) {
        serviceRequestAssigned = assignedData.data?.find(
          (serviceRequest: any) =>
            serviceRequest.id === manualRequest.serviceRequestId
        );
      }

      return {
        id: manualRequest.tmId,
        manualRequestId: manualRequest.id,
        SCH_ARVL_DT: manualRequest.createdOn
          ? new Date(manualRequest.createdOn).toISOString().split("T")[0]
          : "",
        SCH_ARVL_TIME: manualRequest.createdOn
          ? new Date(manualRequest.createdOn).toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })
          : "",
        EMP_ID: manualRequest.tmId,
        isUrgentTransportRequest: true,
        FULL_NAME: "Nil",
        DEPT: "Nil",
        HOTEL_NAME: "Some Hotel",
        empName: "",
        Name: "User",
        assignedInfo: serviceRequestAssigned,
        isManualRequestCreated: true,
        Ship: {},
        FlagsOut: [],
        Port: {},
      };
    });

    return urgentRequestList;
  }, [assignedData, ismanualRequestDetailsLoading, manualRequestDetails]);

  const dataHandling = useCallback(
    (resData: ITmResponse[]) => {
      let newData: any = [];
      const data = _.cloneDeep(resData);

      data.forEach((x) => {
        const y = {
          ...x,
          id: x.EmpId, //uuidv4(),
          isManualRequestCreated:
            (!ismanualRequestDetailsLoading &&
              manualRequestDetails?.data &&
              manualRequestDetails?.data.some(
                (v: any) => v.tmId === x.EmpId
              )) ||
            false,
          assignedInfo:
            !isAssignedDataLoading &&
            assignedData?.data &&
            assignedData?.data.find(
              (v: any) => v.tmId === x.EmpId && subSelectedTab === v.requestType
            ),
          provide_shuttle_service: x?.HotelUnId
            ? shuttleServiceRecords?.data?.[x.HotelUnId] ??
            (shuttleServiceError ? "" : false)
            : false,
        };

        const isShipboardScanned =
          !isShipboardAssignedLoading &&
          shipboardAssigned?.data &&
          shipboardAssigned?.data.every((v: any) => v.tmId !== x.EmpId);

        isShipboardScanned && newData.push(y);
      });

      let finalData = [];

      // Tab based filter
      if (selectedTab === DashboardTabs.ARRIVALS) {
        let newTempData = newData.filter(
          (x: any) => x.isManualRequestCreated === false
        );

        if (
          subSelectedTab ===
          ETransportationServiceRequestTaskType.AIRPORT_TO_HOTEL
          // subSelectedTab === ETransportationServiceRequestTaskType.HOTEL_TO_PORT
        ) {
          finalData = newTempData.filter((x: any) => {
            const arrivalDate = x.SchArrivalDate?.split(" ")?.[0];
            const hotelCheckInDate = x.CheckInDate?.split(" ")?.[0];

            const assignmentDate = x.FutureAssignments?.MovementDate || ""; // YYYY-MM-DD
            const [year, month, date] = assignmentDate.split("-");
            const assignmentDateInHotelFormat = `${month}/${date}/${year}`;

            if (
              arrivalDate !== hotelCheckInDate ||
              assignmentDateInHotelFormat === arrivalDate
            ) {
              return false;
            }

            return x.FLIGHT_FLAG === true && x.HOTEL_FLAG === true;
          });
        }

        if (
          subSelectedTab ===
          ETransportationServiceRequestTaskType.AIRPORT_TO_PORT
        ) {
          finalData = newTempData.filter(
            (x: any) => x.FLIGHT_FLAG === true && x.HOTEL_FLAG === false
          );
        }

        if (
          subSelectedTab === ETransportationServiceRequestTaskType.HOTEL_TO_PORT
        ) {
          finalData = newTempData.filter((x: any) => {
            const arrivalDate = x.SchArrivalDate?.split(" ")?.[0];
            const hotelCheckInDate = x.CheckInDate?.split(" ")?.[0];

            const assignmentDate = x.FutureAssignments?.MovementDate || ""; // YYYY-MM-DD
            const [year, month, date] = assignmentDate.split("-");
            const assignmentDateInHotelFormat = `${month}/${date}/${year}`;

            if (
              arrivalDate !== hotelCheckInDate ||
              assignmentDateInHotelFormat === arrivalDate
            ) {
              return false;
            }
            return x.HOTEL_FLAG === true;
          });
        }

        setRowsLoading(false);
        return finalData;
      }

      if (selectedTab === DashboardTabs.DEPARTURE) {
        let newTempData = newData.filter(
          (x: any) => x.isManualRequestCreated === false
        );

        if (
          subSelectedTab ===
          ETransportationServiceRequestTaskType.PORT_TO_AIRPORT
        ) {
          finalData = newTempData.filter((x: any) => {
            const hotelCheckIn = x?.CheckInDate?.split(" ")?.[0];
            const flightArrival = x?.SchArrivalDate?.split(" ")?.[0];

            if (!hotelCheckIn) {
              return true;
            }

            if (!flightArrival) {
              return false;
            }

            const arrivalDate = moment(flightArrival, "MM/DD/YYYY", true);
            const checkInDate = moment(hotelCheckIn, "MM/DD/YYYY", true);

            if (!arrivalDate.isValid()) {
              return false;
            }

            if (!checkInDate.isValid()) {
              return true;
            }

            return arrivalDate.isBefore(checkInDate, "day");
          });
        }

        if (
          subSelectedTab === ETransportationServiceRequestTaskType.PORT_TO_HOTEL
        ) {
          finalData = newTempData.filter((x: any) => {
            const hotelCheckIn = x?.CheckInDate?.split(" ")?.[0];
            const flightArrival = x?.SchArrivalDate?.split(" ")?.[0];

            if (!hotelCheckIn) {
              return false;
            }

            if (!flightArrival) {
              return true;
            }

            const arrivalDate = moment(flightArrival, "MM/DD/YYYY", true);
            const checkInDate = moment(hotelCheckIn, "MM/DD/YYYY", true);

            if (!arrivalDate.isValid()) {
              return true;
            }

            if (!checkInDate.isValid()) {
              return false;
            }

            return checkInDate.isBefore(arrivalDate, "day");
          });
        }

        setRowsLoading(false);
        return finalData;
      }

      // if (
      //   selectedTab === DashboardTabs.TRANSPORTATION_REQUEST ||
      //   selectedTab === DashboardTabs.MANUAL_REQUEST
      // ) {
      //   finalData = newData.filter(
      //     (x: any) => x.isManualRequestCreated === true
      //   );
      //   const urgentTransportRequests = getUrgentTransportationData();
      //   setRowsLoading(false);
      //   return [...urgentTransportRequests, ...finalData];
      // }

      setRowsLoading(false);
      return newData;
    },
    [
      getUrgentTransportationData,
      selectedTab,
      subSelectedTab,
      assignedData,
      isAssignedDataLoading,
      isShipboardAssignedLoading,
      ismanualRequestDetailsLoading,
      manualRequestDetails,
      shipboardAssigned,
      iSshuttleServiceRecordsLoading,
      shuttleServiceRecords,
    ]
  );

  useEffect(() => {
    setRowsLoading(isLoading || isAssignedDataLoading);

    if (!isLoading && Array.isArray(response?.data)) {
      const filteredResponseData = response.data.filter((item: any) => {
        if (item?.FutureAssignments?.Transportation === "N") {
          return false;
        }

        const shipName = item?.FutureAssignments?.ShipName;

        if (
          !shipName ||
          item.TMDetails?.Department === "SUPERNUM" ||
          item.TMDetails?.Department === "CCL RIDING TEAM"
        ) {
          return false;
        }

        if (typeof shipName === "string") {
          return !shipName.toLowerCase().includes("ccl -");
        }

        return true;
      });

      const processedData = dataHandling(filteredResponseData);
      setRows(processedData);
    }

    // Optional: handle case where response is empty
    if (!isLoading && !response?.data?.length) {
      setRows([]); // explicitly clear if no result
    }
  }, [
    response,
    isLoading,
    assignedData,
    isAssignedDataLoading,
    isShipboardAssignedLoading,
    shipboardAssigned,
    ismanualRequestDetailsLoading,
    manualRequestDetails,
    dataHandling,
  ]);

  const refetchData = (seltab: string) => {
    if (seltab === DashboardTabs.ARRIVALS) {
      refetchAssinged(
        `${API.CARNIVAL_ADMIN.GET_SERVICE_REQUEST_BY_VENDOR_ID
        }?vendorId=${vendorId}&startDate=${assignmentStartDate.format(
          "DD/MM/YYYY"
        )}&endDate=${assignmentEndDate.format("DD/MM/YYYY")}`
      );
    }
    if (seltab === DashboardTabs.DEPARTURE) {
      refetchAssinged(
        `${API.CARNIVAL_ADMIN.GET_SERVICE_REQUEST_BY_VENDOR_ID
        }?vendorId=${vendorId}&startDate=${assignmentStartDate.format(
          "DD/MM/YYYY"
        )}&endDate=${assignmentEndDate.format("DD/MM/YYYY")}`
      );
    }

    if (
      seltab === DashboardTabs.TRANSPORTATION_REQUEST ||
      seltab === DashboardTabs.MANUAL_REQUEST
    ) {
      refetchManualRequest(
        `${API.VENDOR_SERVICE_REQUEST.CREATE_MANUAL_REQUEST}s?vendorId=${vendorId}`
      );
      refetchAssinged(
        `${API.CARNIVAL_ADMIN.GET_SERVICE_REQUEST_BY_VENDOR_ID
        }?vendorId=${vendorId}&startDate=${assignmentStartDate.format(
          "DD/MM/YYYY"
        )}&endDate=${assignmentEndDate.format("DD/MM/YYYY")}`
      );
    }
  };

  return {
    rows,
    isRowsLoading: isRowsLoading || isLoading,
    refetchData,
    handleSearchApply,
    refreshingText,
    error: arrivalDepartureError || error,
    detailedError: arrivalDepartureDetailedError || detailedError,
  };
};
